/**
 * This is the dropdown widget
 *
 * optional @prop widgetId {String} widget ID
 * optional @prop type {String} Type of the widget
 *                valid types are small or medium
 * optional @prop color {string} Color of the widget
 *                valid colors are gray (white is the default)
 * optional @prop selectedValue {String} selected list elem ID
 * optional @prop onSelectedValue {Func} what to do when a user selects a list item
 * optional @prop prefixText {String} prefix text for the selected values
 * optional @prop list {Array} List of dropdown menu items
 *          expected structure is [{ id: ID, value: value }, ... ]
 * optional @prop placeholder {String} Placeholder text
 *
 * return widgetID: {String} widget ID
 *        selectedElemID: {String} selected elem ID
 */
import React from 'react';
import PropTypes from 'prop-types';

import isDeprecated from 'component-lib/isDeprecated';
import displayName from 'component-lib/displayName';

import addTooltip from '../../../lib/AddTooltip';
import DropdownBase from './DropdownBase';

import ArrowUpIcon from '../../Icons/ArrowUpIcon';
import ArrowDownIcon from '../../Icons/ArrowDownIcon';

import styles from './Dropdowns.scss';

export default class Dropdown extends DropdownBase {
    static displayName = displayName('Widgets/Dropdowns/Dropdown');

    static propTypes = {
        prefixText: PropTypes.string,
        selectedValue: PropTypes.string,
        placeholder: PropTypes.string,
    };

    static defaultProps = {
        prefixText: null,
        selectedValue: '',
        placeholder: '',
    };

    constructor(props) {
        super(props);

        isDeprecated(
            Dropdown,
            '{ DropdownSmall, DropdownMedium } from Forms',
        );

        this.openDropdownMenu = this.openDropdownMenu.bind(this);
    }

    addListElemTooltips() {
        this.props.list.forEach((listElem, idx) => {
            if (listElem.id !== this.props.selectedValue) {
                addTooltip(this[`listElem${idx}`]);
            }
        });
    }

    getSelectedElem() {
        const find = listElem => listElem.id === this.props.selectedValue;

        return this.props.list.find(find);
    }

    getInputValue() {
        let inputValue;

        if (this.props.selectedValue.length) {
            inputValue = this.getSelectedElem().value;
        } else {
            inputValue = this.props.placeholder;
        }

        return inputValue;
    }

    renderDropdownMenu() {
        const { color } = this.props;
        const dropdownListStyleName = `dropdown-list${color ? `-${color}` : ''}`;

        return (
            <ol className={styles[dropdownListStyleName]}>
                {
                    this.props.list.map((listElem, idx) => {
                        if (listElem.id === this.props.selectedValue) {
                            return null;
                        }

                        return (
                            <li
                                role="menuitem"
                                key={listElem.id}
                                ref={(c) => { this[`listElem${idx}`] = c; }}
                                onClick={this.onItemClick(listElem.id)}
                                className={styles['list-elem']}
                                data-action={`select-${listElem.id}`}
                            >
                                <data className={styles['list-value']}>
                                    {listElem.value}
                                </data>
                            </li>
                        );
                    })
                }
            </ol>
        );
    }

    render() {
        const { widgetId, selectedValue, prefixText } = this.props;
        const inputValue = this.getInputValue();

        return (
            <div
                {...this.getWidgetProps()}
                data-field={`dropdown-menu-${widgetId}`}
                data-selected-value={this.props.selectedValue}
            >
                <data
                    role="presentation"
                    onClick={this.openDropdownMenu}
                    data-action="toggle-dropdown"
                >
                    {
                        prefixText &&
            !!inputValue.length &&
            <span className={styles['prefix-text']}>
                {prefixText}
            </span>
                    }

                    <span
                        ref={(c) => { this.dropdownInput = c; }}
                        data-value={inputValue}
                        className={styles[this.getInputClass(selectedValue)]}
                    >
                        {inputValue}
                    </span>

                    {this.state.isDropdownEnabled && <ArrowUpIcon className={styles['dropdown-icon']} />}
                    {!this.state.isDropdownEnabled && <ArrowDownIcon className={styles['dropdown-icon']} />}
                </data>

                {this.state.isDropdownEnabled && this.renderDropdownMenu()}
            </div>
        );
    }
}
